import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { listAnimation } from '@core/animations/list.animation';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Maintenance, PagedResult, User } from '@core/interfaces';
import { environment } from '@environment';
import { TranslateService } from '@ngx-translate/core';
import { CreateOrEditPendencyDialogComponent } from './components/create-or-edit-pendency-dialog/create-or-edit-pendency-dialog.component';
import { UpdateAssigneeModalComponent } from './components/update-assignee-modal/update-assignee-modal.component';
import { PendenciesService } from './service/pendencies.service';

@Component({
  selector: 'tab-pendencies',
  templateUrl: './pendencies.component.html',
  styleUrls: ['./pendencies.component.scss'],
  animations: [listAnimation]
})
export class PendenciesComponent implements OnInit {

  @Input() public maintenance: Maintenance;
  public take = 10;
  public after: string | null = null;
  public pendenciesPage: PagedResult = null;
  public enableLoadMoreButton = false;
  public isLoading = false;
  public isLoadingPendencies = false;
  public permissions = {
    canCreate: this.authenticationService.hasPermission('pendencies-create'),
    canEdit: this.authenticationService.hasPermission('pendencies-edit'),
  };

  @ViewChild('pendenciesListElement', { static: false }) pendenciesListElement: ElementRef;

  constructor(
    private readonly matDialog: MatDialog,
    private readonly translateService: TranslateService,
    private readonly pendenciesService: PendenciesService,
    private readonly authenticationService: AuthenticationService
  ) { }

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;

    const where = {
      maintenanceIds: [this.maintenance.id]
    };

    this.pendenciesPage = await this.pendenciesService.getPendencies({
      where,
      take: this.take,
      after: this.after
    });

    this.isLoading = false;
  }

  public async openCreatePendencyDialog(): Promise<void> {
    const dialogRef = this.matDialog.open(CreateOrEditPendencyDialogComponent, {
      panelClass: ['field-dialog-860'],
      data: this.maintenance
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result || !result.pendency || !result.pendency.createPendency) { return; }

    this.pendenciesPage.items.push(result.pendency.createPendency);
    this.pendenciesPage.items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    this.pendenciesPage.totalCount++;

    if (!this.pendenciesListElement || !this.pendenciesListElement.nativeElement) {
      return;
    }

    this.pendenciesListElement.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public async updateAssignee(id?: string, assignee?: User): Promise<void> {
    const dialogRef = this.matDialog.open(UpdateAssigneeModalComponent, {
      data: {
        id,
        assignee
      },
      panelClass: ['update-assignee-modal'],
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result || !result.pendency) { return; }

    const pendency = this.pendenciesPage.items.find((findPendency) => findPendency.id === id);
    pendency.assignee = result.pendency.assignee;
    pendency.updatedAt = result.pendency.updatedAt;
    this.pendenciesPage.items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  public async loadMorePendencies(): Promise<void> {
    this.isLoadingPendencies = true;

    const where = {
      maintenanceIds: [this.maintenance.id]
    };

    const newPendenciesPage = await this.pendenciesService.getPendencies({
      where,
      take: this.take,
      after: this.pendenciesPage.pageInfo.endCursor
    });

    this.pendenciesPage.items = [...this.pendenciesPage.items, ...newPendenciesPage.items];
    this.isLoadingPendencies = false;
    this.enableLoadMoreButton = false;
    const scrollPosition = this.pendenciesListElement.nativeElement.clientHeight / 2;
    this.pendenciesListElement.nativeElement.scrollTo({ top: scrollPosition, behavior: 'smooth' });
  }

  public redirectToPendencyDetails(id: string): void {
    window.location.href = `${environment.MOUNTDOOM_2_URL}/#/pendencias/${id}`;
  }

  public getTooltipText(pendency): string {
    if (!this.permissions.canEdit) {
      return this.translateService.instant('MAINTENANCES.TABS.PENDENCIES.TOOLTIPS.PERMISSION_BLOCK_ASSIGNEE');
    }

    if (pendency.status === 'done') {
      return this.translateService.instant('MAINTENANCES.TABS.PENDENCIES.TOOLTIPS.BLOCK_ASSIGNEE');
    }

    if (pendency.assignee) {
      return this.translateService.instant('MAINTENANCES.TABS.PENDENCIES.TOOLTIPS.ASSIGNEE', { assigneeName: pendency.assignee.name });
    }

    return this.translateService.instant('MAINTENANCES.TABS.PENDENCIES.TOOLTIPS.NO_ASSIGNEE');
  }

  @HostListener('scroll', ['$event'])
  public async onScroll(event: any): Promise<void> {
    const target = event.target;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const hasMorePendencies = this.pendenciesPage.totalCount > this.pendenciesPage.items.length;

    if (scrollPosition >= target.scrollHeight && hasMorePendencies) {
      this.enableLoadMoreButton = true;
      return;
    }

    this.enableLoadMoreButton = false;
  }
}
